import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, session, unwrap } from '../api/client';
import { useAccounts } from '../api/hooks';
import type { AccountStats, Funnel, Items } from '../api/types';
import { useSession } from '../auth/session';
import { dayAfter } from '../components/calendar';
import { Card, DataTable, ErrorAlert, Field, PageHeader, useToast } from '../components/ui';

const REPORTS: ReadonlyArray<[string, string, string]> = [
  [
    'automations',
    'Automation performance',
    'Users triggered, messages sent, clicks and conversions per automation',
  ],
  [
    'messages',
    'Message analytics',
    'Daily sent, accepted by Instagram, failed and clicks; covers the selected range (the last 30 days when none is set)',
  ],
  [
    'funnel',
    'Funnel analytics',
    'Incoming interactions vs eligible vs DM sent vs clicked vs conversions',
  ],
  ['contacts', 'Contacts analysis', 'Contact list with tags, sources and attribution'],
  ['api-usage', 'API usage report', 'Calls per day and per API key'],
  ['flows', 'Flow performance', 'Sessions started, completed, handed off and active per flow'],
  ['posts', 'Post level', 'Comments, unique commenters, runs and DMs sent per post or reel'],
  ['stories', 'Story level', 'Replies, unique users, runs and DMs sent per story'],
];

export function ReportsPage() {
  const toast = useToast();
  // ANA-13 (revised): link figures are a Legend-and-above benefit
  const { me } = useSession();
  const links = me?.features.linkAnalytics !== false;
  // RPT-11: a date range for the funnel, the per-account table and the downloads; empty = all time
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  // RPT-11 (revised): the "To" day is inclusive; the API's upper bound is exclusive, so ask for the next day
  const rangeQs = qs({
    from: range.from || undefined,
    to: range.to ? dayAfter(range.to) : undefined,
  });
  const funnel = useQuery({
    queryKey: ['funnel', rangeQs],
    queryFn: () => unwrap(api.get<Funnel>(`/portal/analytics/funnel${rangeQs}`)),
  });
  const accounts = useQuery({
    queryKey: ['account-stats', rangeQs],
    queryFn: () => unwrap(api.get<Items<AccountStats>>(`/portal/analytics/accounts${rangeQs}`)),
  });
  const socials = useAccounts();
  const accountLabel = (aid: string): string => {
    const u = socials.data?.items.find((x) => x.id === aid)?.username;
    return u ? `@${u}` : aid.slice(-8);
  };
  const download = async (name: string) => {
    const res = await fetch(`/portal/reports/${name}.csv${rangeQs}`, {
      headers: { authorization: `Bearer ${session.get() ?? ''}` },
    });
    if (!res.ok) {
      // RPT-11 (revised): the server's reason, not only the status
      const reason = (await res.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      return toast(reason?.error?.message ?? `Download failed (${res.status})`, 'error');
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const f = funnel.data;
  const max = Math.max(1, f?.interactions ?? 1);
  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Exportable analytics for automations, messages, funnels, flows, posts, stories, contacts and API usage"
      />
      <div className="row" style={{ marginBottom: 12 }}>
        <Field label="From">
          <input
            className="input"
            type="date"
            value={range.from}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
          />
        </Field>
        <Field label="To">
          <input
            className="input"
            type="date"
            value={range.to}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
          />
        </Field>
      </div>
      <ErrorAlert error={funnel.error} />
      <div className="grid grid-2">
        <Card title="Funnel">
          {f ? (
            <div className="grid" style={{ gap: 8 }}>
              {(
                [
                  ['Incoming interactions', f.interactions],
                  ['Automation eligible', f.eligible],
                  ['DM sent', f.dmsSent],
                  ...(links
                    ? ([
                        ['Clicked', f.clicked ?? 0],
                        ['Converted', f.converted ?? 0],
                      ] as const)
                    : []),
                ] as const
              ).map(([label, v]) => (
                <div key={label}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{label}</span>
                    <strong>{v}</strong>
                  </div>
                  <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 4 }}>
                    <div
                      style={{
                        width: `${Math.max(2, (v / max) * 100)}%`,
                        height: 8,
                        background: 'var(--primary)',
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </Card>
        <Card title="Per account">
          <ErrorAlert error={accounts.error} />
          <DataTable>
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Interactions</th>
                  <th>Runs</th>
                  <th>Sent</th>
                  {links ? <th>Clicks</th> : null}
                  {links ? <th>Conversions</th> : null}
                </tr>
              </thead>
              <tbody>
                {accounts.data?.items.length === 0 ? (
                  <tr>
                    <td colSpan={links ? 6 : 4} className="muted">
                      No connected accounts yet.
                    </td>
                  </tr>
                ) : null}
                {(accounts.data?.items ?? []).map((a) => (
                  <tr key={a.accountId}>
                    <td>{accountLabel(a.accountId)}</td>
                    <td>{a.interactions}</td>
                    <td>{a.runs}</td>
                    <td>{a.messagesSent}</td>
                    {links ? <td>{a.clicks ?? 0}</td> : null}
                    {links ? <td>{a.conversions ?? 0}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        </Card>
      </div>
      <Card title="Downloads">
        <DataTable>
          <table className="table">
            <tbody>
              {REPORTS.map(([name, title, text]) => (
                <tr key={name}>
                  <td>
                    <strong>{title}</strong>
                    <div className="small muted">
                      {text}
                      {!links && /click|conversion/i.test(text)
                        ? ' Clicks and conversions are reported from Legend; this plan gets the other columns.'
                        : ''}
                    </div>
                  </td>
                  <td className="right">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => void download(name)}
                    >
                      Download CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTable>
      </Card>
    </>
  );
}
